
// src/services/speech/TextToSpeechService.ts
import { supabase } from '@/integrations/supabase/client';

export interface TextToSpeechProps {
  text: string;
  context?: string;
  question?: string;
  answer?: string;
  intervieweeName?: string;
  title?: string;
  company?: string;
  aiSummary?: string;
  aiResponse?: string;
  // Add GCP TTS specific options
  voiceName?: string;
  languageCode?: string;
}

export class TextToSpeechService {
  private static audioCache: Map<string, { audioUrl: string; duration: number }> = new Map(); // Frontend cache for audio data
  
  // Warmup function to prevent cold starts
  private static isWarmedUp = false;
  
  // ALWAYS rebuild to correct public URL format if host is our Supabase
  private static normalizeAudioUrl(url: string): string {
    try {
      if (!url) return url;
      const u = new URL(url);
      if (!u.hostname.includes('supabase.co')) return url;

      // Already correct?
      if (u.pathname.includes('/storage/v1/object/public/tts-audio/')) return url;

      // Rebuild from filename with correct path
      const filename = u.pathname.split('/').pop();
      if (!filename) return url;

      return `https://vjrredluydfrknkcclbw.supabase.co/storage/v1/object/public/tts-audio/${filename}`;
    } catch {
      return url;
    }
  }
  
  static async warmupEdgeFunction(): Promise<void> {
    if (this.isWarmedUp) return;
    
    try {
      console.log('Warming up TTS edge function...');
      await supabase.functions.invoke('text-to-speech', {
        body: { text: 'warmup', voiceName: 'nb-NO-Chirp3-HD-Callirrhoe', languageCode: 'nb-NO' }
      });
      this.isWarmedUp = true;
      console.log('TTS edge function warmed up');
    } catch (error) {
      console.warn('Failed to warm up TTS edge function:', error);
    }
  }

  // Changed to use new cached TTS system
  static async generateSpeech(
    text: string, 
    voiceName: string = 'nb-NO-Chirp3-HD-Callirrhoe',
    languageCode: string = 'nb-NO',
    forceRegenerate: boolean = false
  ): Promise<{ audioUrl: string; duration: number } | null> {
    if (!text?.trim()) return null;

    const startTime = Date.now();
    const textPreview = text.substring(0, 100) + (text.length > 100 ? '...' : '');
    
    try {
      console.log(`🎤 TTS REQUEST: "${textPreview}" (${text.length} chars, voice: ${voiceName}, force: ${forceRegenerate})`);
      
      // Use the text-to-speech function that caches to storage
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { 
          text, 
          voiceName, 
          languageCode,
          forceRegenerate
        }
      });

      const responseTime = Date.now() - startTime;

      if (error) {
        console.error(`❌ TTS FAILED: ${error.message} (${responseTime}ms)`);
        throw error;
      }

      if (data?.success && data?.audioUrl) {
        const normalizedUrl = this.normalizeAudioUrl(data.audioUrl);
        const result = { 
          audioUrl: normalizedUrl, 
          duration: data.duration || 60 // fallback duration
        };
        
        const source = data.cached ? '📀 CACHE HIT' : '🔊 NEW GENERATION';
        const costIndicator = data.cached ? '(FREE)' : '(GOOGLE API COST)';
        
        console.log(`${source}: "${textPreview}" → ${result.duration}s ${costIndicator} (${responseTime}ms)`);
        console.log(`🔗 Audio URL: ${result.audioUrl}`);
        
        return result;
      }
      
      console.error(`❌ TTS INVALID RESPONSE: ${JSON.stringify(data)} (${responseTime}ms)`);
      return null;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      console.error(`💥 TTS ERROR: ${error.message} (${responseTime}ms)`);
      throw error;
    }
  }

  static formatTextForSpeech(props: TextToSpeechProps): string {
    if (!props.answer && !props.text) {
      return "No content available to speak.";
    }

    const parts = [];
    
    // Extract only the headline from context (first line before any newlines or long content)
    if (props.context) {
      const contextLines = props.context.split('\n');
      const headline = contextLines[0].trim();
      // Only add if it's not too long (likely a headline, not article content)
      if (headline.length > 0 && headline.length < 200) {
        parts.push(headline);
      }
    }
    
    // Add question directly without prefix
    if (props.question) {
      parts.push(props.question);
    }
    
    // Add interviewee information
    if (props.intervieweeName && props.title && props.company) {
      parts.push(`${props.intervieweeName}, ${props.title}, ${props.company}`);
    }
    
    // Add the answer
    if (props.answer) {
      parts.push(props.answer);
    }
    
    // Add AI summary with proper Norwegian labels
    if (props.aiSummary) {
      parts.push("Oppsummert");
      const summaryPoints = props.aiSummary
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      parts.push("Sammendrag av Kai");
      parts.push(summaryPoints.join('. '));
    }
    
    // Add AI response if available
    if (props.aiResponse) {
      parts.push(props.aiResponse);
    }
    
    // Fallback to text if nothing else is available
    if (parts.length === 0 && props.text) {
      parts.push(props.text);
    }
    
    return parts.join('. ');
  }

  // Get audio duration from URL with faster loading
  static async getAudioDuration(audioUrl: string): Promise<number> {
    return new Promise((resolve) => {
      const audio = new Audio(audioUrl);
      
      // Set up timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.warn('Audio duration detection timed out');
        resolve(0);
      }, 3000);
      
      audio.addEventListener('loadedmetadata', () => {
        clearTimeout(timeout);
        resolve(Math.ceil(audio.duration)); // Return duration in seconds, rounded up
      });
      
      audio.addEventListener('error', () => {
        clearTimeout(timeout);
        console.warn('Audio duration detection failed');
        resolve(0); // Fallback to 0 if duration can't be determined
      });
      
      // Preload metadata only (faster than full audio)
      audio.preload = 'metadata';
    });
  }

  // Simplified function to play audio directly from URL
  static async playAudioFromUrl(audioUrl: string): Promise<HTMLAudioElement> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);
      audio.preload = 'auto'; // Optimal for quick playback

      audio.oncanplaythrough = () => {
        // Audio file is ready to play
        resolve(audio);
      };

      audio.onerror = (e) => {
        console.error("Audio element error:", e);
        reject(new Error('Audio playback failed from URL.'));
      };
      
      // If for some reason oncanplaythrough doesn't trigger,
      // and it's still ready to play
      if (audio.readyState >= 3) { // HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
        resolve(audio);
      }
    });
  }
}
